import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface AppVersion {
  id: number;
  appVersion: string;
  versionDate: string;
  forceUpdate: boolean;
  isCurrent: boolean;
}

export function AppInfoTab() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newVersion, setNewVersion] = useState({
    appVersion: "",
    forceUpdate: false,
    isCurrent: false,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: versions = [] } = useQuery<AppVersion[]>({
    queryKey: ["/api/appinfo"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/appinfo");
      return response.json();
    },
  });

  const addVersionMutation = useMutation({
    mutationFn: async (data: typeof newVersion) => {
      const response = await apiRequest("POST", "/api/appinfo", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appinfo"] });
      setIsAddDialogOpen(false);
      setNewVersion({ appVersion: "", forceUpdate: false, isCurrent: false });
      toast({
        title: "Version added successfully",
        description: "The new app version has been added.",
      });
    },
  });

  const updateVersionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<AppVersion> }) => {
      const response = await apiRequest("PATCH",`/api/appinfo/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appinfo"] });
      toast({
        title: "Version updated successfully",
        description: "The app version has been updated.",
      });
    },
  });

  const deleteVersionMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest("DELETE", `/api/appinfo/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appinfo"] });
      toast({
        title: "Version deleted successfully",
        description: "The app version has been deleted.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addVersionMutation.mutate(newVersion);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">App Versions</h2>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>Add New Version</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Version</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="appVersion">Version</label>
                <Input
                  id="appVersion"
                  value={newVersion.appVersion}
                  onChange={(e) =>
                    setNewVersion({ ...newVersion, appVersion: e.target.value })
                  }
                  placeholder="e.g., 1.0.0"
                  required
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="forceUpdate"
                  checked={newVersion.forceUpdate}
                  onCheckedChange={(checked) =>
                    setNewVersion({
                      ...newVersion,
                      forceUpdate: checked as boolean,
                    })
                  }
                />
                <label htmlFor="forceUpdate">Force Update</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isCurrent"
                  checked={newVersion.isCurrent}
                  onCheckedChange={(checked) =>
                    setNewVersion({
                      ...newVersion,
                      isCurrent: checked as boolean,
                    })
                  }
                />
                <label htmlFor="isCurrent">Current Version</label>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Add Version</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Version</TableHead>
            <TableHead>Release Date</TableHead>
            <TableHead>Force Update</TableHead>
            <TableHead>Current</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map((version) => (
            <TableRow key={version.id}>
              <TableCell>{version.appVersion}</TableCell>
              <TableCell>
                {format(new Date(version.versionDate), "MMM d, yyyy")}
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={version.forceUpdate}
                  onCheckedChange={(checked) =>
                    updateVersionMutation.mutate({
                      id: version.id,
                      data: { forceUpdate: checked as boolean },
                    })
                  }
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={version.isCurrent}
                  onCheckedChange={(checked) =>
                    updateVersionMutation.mutate({
                      id: version.id,
                      data: { isCurrent: checked as boolean },
                    })
                  }
                />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteVersionMutation.mutate(version.id)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}